import { useCallback, useMemo } from 'react'
import {
  Background,
  Controls,
  Handle,
  Panel,
  Position,
  ReactFlow,
  type Connection,
  type NodeProps,
  type NodeTypes,
} from '@xyflow/react'
import type { NodeDefinitionView, PortView } from './definitions.js'
import {
  toFlowEdges,
  toFlowNodes,
  withConnection,
  withPosition,
  withoutNode,
  type CanvasNodeData,
  type Graph,
} from './graph.js'
import { layout } from './layout.js'

/**
 * Чем кончился шаг узла в последнем прогоне и сколько элементов он отдал.
 * Собирается из прогона (RunLog.итогиУзловИз) и приходит сюда готовым:
 * холст не читает службу.
 */
export interface NodeOutcomeView {
  status: string
  items: number
}

export interface CanvasProps {
  graph: Graph
  definitions: readonly NodeDefinitionView[]
  onGraphChange: (graph: Graph) => void
  /** Сколько замечаний валидатора у каждого узла (FR-UI-001). */
  issuesByNode?: Readonly<Record<string, number>>
  /** Чем кончился шаг каждого узла в открытом прогоне (FR-ADM-034). */
  outcomeByNode?: Readonly<Record<string, NodeOutcomeView>>
  selectedNodeId?: string | null
  onSelect?: (nodeId: string | null) => void
}

/** Описание нод по типу и версии — узлу нужны его порты. */
export interface PortsByType {
  inputs: readonly PortView[]
  outputs: readonly PortView[]
}

/**
 * Порты и описания нод — общей таблицей рядом с компонентом: он получает от
 * React Flow только свои данные и до списка описаний дотянуться не может.
 * Описания за сеанс не меняются, поэтому таблицы достаточно.
 *
 * Замечания и исход прогона так хранить нельзя: они меняются на живом
 * экране, а React Flow перерисовывает узел только при смене его данных —
 * значение, пришедшее в обход них, ждало бы следующей правки графа. Они
 * едут в `CanvasNodeData`.
 */
const PORTS: { current: Map<string, PortsByType> } = { current: new Map() }

function portsOf(type: string, version: number): PortsByType {
  return PORTS.current.get(`${type}@${version}`) ?? { inputs: [], outputs: [] }
}

/**
 * Как нода называется по-человечески и к какой группе относится. Хранится
 * тем же приёмом, что и порты: компонент узла получает от React Flow
 * только свои данные и до описаний дотянуться не может.
 */
const ОПИСАНИЯ: { current: Map<string, { displayName: string; group: string }> } = {
  current: new Map(),
}

function описаниеУзла(type: string, version: number): { displayName: string; group: string } {
  return ОПИСАНИЯ.current.get(`${type}@${version}`) ?? { displayName: type, group: '' }
}

/**
 * Кромка узла по виду работы: триггер, отправка в канал, ветвление,
 * остальное. Вид узнаётся раньше названия — по цвету слева.
 *
 * Каналы делят одну кромку намеренно: цвет здесь означает род работы,
 * а не то, куда именно уходит сообщение. Какой это канал, видно из
 * названия узла — «Отправить текст Telegram», — и второй цвет ради того
 * же самого только размыл бы смысл первого.
 */
function видУзла(group: string): string {
  if (group === 'trigger') return ' узел--триггер'
  if (group === 'whatsapp' || group === 'telegram') return ' узел--канал'
  if (group === 'flow') return ' узел--поток'
  return ''
}

/** Понятное название исхода шага: `running` человеку ничего не говорит. */
const ИСХОДЫ: Readonly<Record<string, string>> = {
  running: 'идёт',
  success: 'успешно',
  failed: 'с ошибкой',
  waiting: 'ждёт',
  skipped: 'пропущен',
  cancelled: 'отменён',
}

/**
 * Цвет исхода — тот же, что у состояний по всему порталу: зелёный работает,
 * синий идёт, жёлтый ждёт человека, красный сломан (правило «цвет означает
 * состояние», шапка styles.css). Своей палитры у холста нет намеренно: узел,
 * покрашенный иначе, чем ярлык номера, пришлось бы читать заново.
 */
const ТОН_ИСХОДА: Readonly<Record<string, string>> = {
  running: 'progress',
  success: 'ok',
  failed: 'danger',
  waiting: 'warn',
  cancelled: 'warn',
  skipped: 'neutral',
}

export function названиеИсхода(status: string): string {
  return ИСХОДЫ[status] ?? status
}

export function тонИсхода(status: string): string {
  return ТОН_ИСХОДА[status] ?? 'neutral'
}

/**
 * Подписывать ли порты этой стороны. Подпись появляется там, где выбор
 * есть: у «Условия» два выхода, у «Развилки» пять, и без подписи по графу
 * нельзя сказать, какая ветвь куда ведёт (FR-ADM-033). Единственный «Выход»
 * подписывать нечем — он и так единственный, а надпись на каждой карточке
 * съедает то внимание, ради которого подписи и заведены.
 */
function подписыватьПорты(ports: readonly PortView[]): boolean {
  return ports.length > 1
}

/** Порт с подписью и ручкой связи. */
function Порт({
  port,
  nodeId,
  сторона,
  подписывать,
}: {
  port: PortView
  nodeId: string
  сторона: 'in' | 'out'
  подписывать: boolean
}): React.JSX.Element {
  const роль = сторона === 'in' ? 'Вход' : 'Выход'
  // Подпись читается и глазами, и диктором, и при наведении: три способа
  // спросить одно и то же, и ни один из них не должен молчать.
  const подпись = `${роль}: ${port.displayName}`

  return (
    <li className={`узел__порт узел__порт--${сторона}${port.portType === 'error' ? ' узел__порт--отказ' : ''}`}>
      <Handle
        type={сторона === 'in' ? 'target' : 'source'}
        position={сторона === 'in' ? Position.Left : Position.Right}
        id={port.name}
        data-testid={`handle-${nodeId}-${port.name}-${сторона}`}
        title={подпись}
        aria-label={подпись}
      />
      {подписывать ? <span className="узел__порт-подпись">{port.displayName}</span> : null}
    </li>
  )
}

/**
 * Один компонент на все ноды: их внешний вид строится по описанию, а не
 * пишется для каждой отдельно (FR-NODE-001). Иначе добавление ноды в службе
 * требовало бы правки редактора.
 */
function ScenarioNode({ data, selected }: NodeProps): React.JSX.Element {
  const { node, issues = 0, outcome: итог } = data as unknown as CanvasNodeData
  const ports = portsOf(node.type, node.typeVersion)
  const описание = описаниеУзла(node.type, node.typeVersion)

  return (
    <div
      className={
        `узел${видУзла(описание.group)}` +
        `${selected === true ? ' узел--выбран' : ''}` +
        `${node.disabled === true ? ' узел--выключен' : ''}` +
        `${issues > 0 ? ' узел--с-ошибкой' : ''}` +
        `${итог === undefined ? '' : ` узел--шаг-${тонИсхода(итог.status)}`}`
      }
      data-testid={`node-${node.id}`}
    >
      <strong>{node.name}</strong>
      {/* Что узел делает, словами автора сценария. Раньше здесь стоял тип
          из реестра («whatsapp-send-text»): внутреннее имя, которого нет
          в словаре человека, собирающего сценарий. */}
      <span className="узел__тип">{описание.displayName}</span>

      {issues > 0 ? (
        <span
          className="узел__ошибка"
          data-testid={`node-error-${node.id}`}
          title={`Замечаний валидатора: ${issues}`}
        >
          {issues}
        </span>
      ) : null}

      {/* Исход шага прямо на карточке (FR-ADM-034). До этого прогон менял
          только полосу под холстом: граф после запуска выглядел ровно так
          же, как до него, — и какая ветвь сработала, приходилось выяснять
          построчным чтением журнала. Узел, которого в прогоне нет, молчит:
          его молчание и есть ответ — ветвь не сработала. */}
      {итог !== undefined ? (
        <span
          className={`узел__итог узел__итог--${тонИсхода(итог.status)}`}
          data-testid={`node-outcome-${node.id}`}
          title={`Шаг прогона: ${названиеИсхода(итог.status)}. Элементов на выходе: ${итог.items}`}
        >
          {`${названиеИсхода(итог.status)} · ${итог.items}`}
        </span>
      ) : null}

      <div className="узел__порты">
        <ul className="узел__порты-колонка узел__порты-колонка--вход">
          {ports.inputs.map((port) => (
            <Порт
              key={port.name}
              port={port}
              nodeId={node.id}
              сторона="in"
              подписывать={подписыватьПорты(ports.inputs)}
            />
          ))}
        </ul>
        <ul className="узел__порты-колонка узел__порты-колонка--выход">
          {ports.outputs.map((port) => (
            <Порт
              key={port.name}
              port={port}
              nodeId={node.id}
              сторона="out"
              подписывать={подписыватьПорты(ports.outputs)}
            />
          ))}
        </ul>
      </div>
    </div>
  )
}

const NODE_TYPES: NodeTypes = { сценарный: ScenarioNode }

/** Кегль имени узла на карточке: `.узел strong` наследует `--font-size-sm`. */
export const РАЗМЕР_ИМЕНИ_ПХ = 13
/** Ниже этого размера на экране текст перестают читать, а начинают угадывать. */
export const ПОРОГ_ЧТЕНИЯ_ПХ = 10

/**
 * Пределы подбора масштаба у показа целиком (FR-UI-013).
 *
 * Нижний порог берётся из читаемости имени: семь узлов подряд уводили
 * подбор к 0,55, и имя в 13 px становилось семью пикселями на экране.
 * Что не поместилось — доступно прокруткой: нечитаемый обзор хуже
 * частичного. Верхний предел — единица: приближать сценарий из трёх
 * узлов на весь экран незачем.
 *
 * Ограничен только автоматический подбор. Руками отдалять по-прежнему
 * можно: `minZoom` самого холста не трогаем.
 */
export const ОБЗОР = { minZoom: 0.8, maxZoom: 1 } as const

/**
 * Подписи холста для программ чтения с экрана.
 *
 * Свои кнопки библиотека подписывает по-английски (Zoom In, Fit View,
 * Toggle Interactivity), и диктор произносит их чужим языком посреди
 * русского интерфейса — то же самое, что англоязычная надпись на кнопке,
 * только слышит её лишь тот, кто читает экран ушами.
 *
 * Задаются на `ReactFlow`, а не на `Controls`: у самого `Controls` из
 * свойств есть только `aria-label` всей группы, а подписи кнопок он берёт
 * из общей настройки холста — оттуда же их берут мини-карта, порты и
 * описания узлов, так что переводится всё разом.
 */
const ПОДПИСИ_ХОЛСТА = {
  'controls.ariaLabel': 'Управление холстом',
  'controls.zoomIn.ariaLabel': 'Приблизить',
  'controls.zoomOut.ariaLabel': 'Отдалить',
  'controls.fitView.ariaLabel': 'Показать сценарий целиком',
  'controls.interactive.ariaLabel': 'Запретить или разрешить правку холста',
  'minimap.ariaLabel': 'Карта сценария',
  'handle.ariaLabel': 'Порт узла',
  'node.a11yDescription.default':
    'Нажмите Enter или пробел, чтобы выбрать узел. Delete удаляет его, Escape отменяет.',
  'node.a11yDescription.keyboardDisabled':
    'Нажмите Enter или пробел, чтобы выбрать узел. Дальше его двигают стрелками. Delete удаляет узел, Escape отменяет.',
  'edge.a11yDescription.default':
    'Нажмите Enter или пробел, чтобы выбрать связь. Delete удаляет её, Escape отменяет.',
}

/**
 * Кого выбрали в этом пакете изменений: узел, «никого» или `undefined`,
 * если про выбор пакет ничего не сказал.
 *
 * Решение принимается один раз на весь пакет, и это главное. Переключение
 * с узла на узел приходит от библиотеки двумя изменениями сразу — снятие
 * выбора со старого и установка на новый, — а порядок в пакете свой
 * у каждого случая. Обработка «по последнему» возвращала выбор назад:
 * первый узел выбирался, второй иногда тоже, третий — уже никогда, и
 * панель намертво показывала чужие параметры. Лечилось перезагрузкой.
 *
 * Установка важнее снятия: снятие со старого в том же пакете означает
 * лишь то, что выбрали другой. Снятие без установки — щелчок по пустому
 * холсту, и это настоящее «никого».
 */
export function выборИзПакета(
  changes: readonly { type: string; id?: string; selected?: boolean }[],
): string | null | undefined {
  let выбран: string | null | undefined
  for (const change of changes) {
    if (change.type !== 'select' || change.id === undefined) continue
    if (change.selected === true) выбран = change.id
    else if (выбран === undefined) выбран = null
  }
  return выбран
}

/**
 * Холст сценария. Правит тот же `Graph`, что хранит служба: собственного
 * формата у редактора нет.
 */
export function Canvas({
  graph,
  definitions,
  onGraphChange,
  issuesByNode,
  outcomeByNode,
  selectedNodeId,
  onSelect,
}: CanvasProps): React.JSX.Element {
  // Порты складываются в общую таблицу: компонент узла получает от React
  // Flow только свои данные и не может дотянуться до списка описаний.
  PORTS.current = useMemo(() => {
    const table = new Map<string, PortsByType>()
    for (const definition of definitions) {
      table.set(`${definition.type}@${definition.version}`, {
        inputs: definition.inputs,
        outputs: definition.outputs,
      })
    }
    return table
  }, [definitions])

  // Тем же приёмом — человеческое имя и группа ноды: узел на холсте
  // показывает, что он делает, а не свой тип из реестра.
  ОПИСАНИЯ.current = useMemo(() => {
    const table = new Map<string, { displayName: string; group: string }>()
    for (const definition of definitions) {
      table.set(`${definition.type}@${definition.version}`, {
        displayName: definition.displayName,
        group: definition.group,
      })
    }
    return table
  }, [definitions])

  // Замечания и исход прогона кладутся в данные узла, а не читаются
  // компонентом из таблицы рядом: React Flow перерисовывает узел только при
  // смене его данных, и значение в обход них доезжало бы до экрана лишь со
  // следующей правкой графа.
  const nodes = useMemo(
    () =>
      toFlowNodes(graph).map((node) => ({
        ...node,
        selected: node.id === selectedNodeId,
        data: {
          ...node.data,
          issues: issuesByNode?.[node.id] ?? 0,
          ...(outcomeByNode?.[node.id] === undefined
            ? {}
            : { outcome: outcomeByNode[node.id] }),
        },
      })),
    [graph, selectedNodeId, issuesByNode, outcomeByNode],
  )
  const edges = useMemo(
    () =>
      toFlowEdges(graph, (node, output) => {
        const ports = portsOf(node.type, node.typeVersion)
        if (!подписыватьПорты(ports.outputs)) return undefined
        return ports.outputs.find((port) => port.name === output)?.displayName
      }),
    [graph, definitions],
  )

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (connection.source === null || connection.target === null) return
      onGraphChange(
        withConnection(graph, {
          from: { node: connection.source, output: connection.sourceHandle ?? 'main', index: 0 },
          to: { node: connection.target, input: connection.targetHandle ?? 'main', index: 0 },
        }),
      )
    },
    [graph, onGraphChange],
  )

  const handleNodesChange = useCallback(
    (
      changes: {
        type: string
        id?: string
        selected?: boolean
        position?: { x: number; y: number }
      }[],
    ): void => {
      let next = graph

      for (const change of changes) {
        if (change.type === 'position' && change.id !== undefined && change.position !== undefined) {
          next = withPosition(next, change.id, change.position)
        }
        if (change.type === 'remove' && change.id !== undefined) {
          next = withoutNode(next, change.id)
        }
      }

      const выбран = выборИзПакета(changes)
      if (выбран !== undefined) onSelect?.(выбран)
      if (next !== graph) onGraphChange(next)
    },
    [graph, onGraphChange, onSelect],
  )

  /*
   * Раскладка по связям — по команде, а не сама. Расставленный руками
   * граф это работа автора: переставлять узлы при каждой правке значит
   * стирать её (FR-UI-012).
   */
  const разложить = useCallback(() => {
    const места = layout(graph)
    onGraphChange({
      ...graph,
      nodes: graph.nodes.map((node) => {
        const место = места.get(node.id)
        return место === undefined ? node : { ...node, position: [место.x, место.y] }
      }),
    })
  }, [graph, onGraphChange])

  return (
    <div className="холст" data-testid="canvas" style={{ width: '100%', height: '100%' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        onConnect={handleConnect}
        onNodesChange={handleNodesChange as never}
        ariaLabelConfig={ПОДПИСИ_ХОЛСТА}
        fitView
        /*
         * Ниже 0,8 показ целиком смысла не имеет: имя узла набрано 13 px,
         * при 0,55 это 7 px на экране, а порог чтения около 10. Что не
         * поместилось — доступно прокруткой: нечитаемый обзор хуже
         * частичного (FR-UI-013).
         */
        fitViewOptions={ОБЗОР}
      >
        <Background />
        <Panel position="top-right">
          <button type="button" className="btn btn--small" onClick={разложить}>
            Разложить
          </button>
        </Panel>
        {/* Знак «React Flow» в углу холста оставлен намеренно: библиотека
            бесплатная, и упоминание — то немногое, чем мы за неё платим. */}
        <Controls />
      </ReactFlow>
    </div>
  )
}
